import type { LocationQuery, RouteLocationNormalized, RouteLocationRaw } from 'vue-router'
import { AGENDA_STATE_FIELDS } from '@/composables/useAgendaUrlState'

/**
 * Restores the agenda's view-state when the user returns to it through an
 * in-app link (#3244).
 *
 * `useAgendaUrlState` already serialises date, granularity, mode and the
 * combined-view filters into the query string, so the browser back button
 * restores the agenda as the user left it. In-app links do not: the breadcrumb
 * on the appointment detail page and the sidebar nav item both navigate to
 * `{ name: 'agenda' }` with no query, which resets the agenda to today / week /
 * personal / no filters.
 *
 * The fix is a snapshot of the query the user last left the agenda with, laid
 * back over any param-less arrival. Deliberately memory-only — no
 * localStorage/sessionStorage — so a reload or a fresh tab still opens on
 * today; only mid-session round-trips restore.
 *
 * Nothing here touches `useAgendaUrlState`: the restore happens as a redirect
 * before the view mounts, so its instances hydrate from the restored URL
 * exactly as they do from a deep link (#3161, #3185, #3188).
 */

/** The query the user last left the agenda with, reduced to the agenda params. */
let lastAgendaQuery: LocationQuery = {}

/** The part of a resolved route this module reads. */
type AgendaRoute = Pick<RouteLocationNormalized, 'name' | 'query'>

/** The agenda-owned params of `query`, dropping everything else. */
function agendaParams(query: LocationQuery): LocationQuery {
  const params: LocationQuery = {}
  for (const field of AGENDA_STATE_FIELDS) {
    if (query[field] !== undefined) {
      params[field] = query[field]
    }
  }
  return params
}

/**
 * Remember the agenda's view-state as the user navigates away from it.
 *
 * Ignores navigations that stay on the agenda: those are the debounced
 * `router.replace` calls `useAgendaUrlState` makes on every filter change, and
 * re-snapshotting on each of them is churn for a value only read on the way
 * back in.
 *
 * Safe to call for failed navigations too — an aborted leave keeps the user on
 * the agenda with the very query that was just snapshotted.
 */
export function captureAgendaViewState(to: AgendaRoute, from: AgendaRoute): void {
  if (from.name !== 'agenda' || to.name === 'agenda') {
    return
  }
  lastAgendaQuery = agendaParams(from.query)
}

/**
 * Where a navigation onto the agenda should go instead, or `null` to let it
 * through.
 *
 * Only param-less arrivals are redirected, so a deep link that names its own
 * state (`/agenda?date=2026-08-03`) is never overridden. `replace: true` keeps
 * the restore out of the history stack: back from the restored agenda returns
 * to the page the user came from, not to the param-less agenda entry.
 */
export function restoreAgendaViewState(to: AgendaRoute): RouteLocationRaw | null {
  const hasAgendaParams = Object.keys(agendaParams(to.query)).length > 0
  if (hasAgendaParams || Object.keys(lastAgendaQuery).length === 0) {
    return null
  }
  return { name: 'agenda', query: { ...to.query, ...lastAgendaQuery }, replace: true }
}
